import { useMemo, useRef, useState } from "react";
import { Button, Offcanvas } from "react-bootstrap";
import { BsDownload, BsGraphUpArrow, BsQuestionCircle } from "react-icons/bs";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Label,
  LabelList,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

const PERIOD_A_COLOR = "#38bdf8";
const PERIOD_B_COLOR = "#45cc93";

function escapeXml(value) {
  return String(value || "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&apos;");
}

function sanitizeFileName(value) {
  return String(value || "chart")
    .toLowerCase()
    .replace(/[^a-zа-я0-9]+/gi, "_")
    .replace(/^_+|_+$/g, "");
}

function downloadChartSvg(container, fileName, meta = {}) {
  const svg = container?.querySelector("svg");
  if (!svg) return;

  const rect = container.getBoundingClientRect();
  const chartWidth = Math.max(720, Math.round(rect.width || 960));
  const chartHeight = Math.max(320, Math.round(rect.height || 420));
  const title = escapeXml(meta.title || "График");
  const subtitle = escapeXml(meta.subtitle || "");
  const scopeLabel = escapeXml(meta.scopeLabel || "");
  const headerHeight = subtitle || scopeLabel ? 92 : 64;
  const exportWidth = chartWidth;
  const exportHeight = chartHeight + headerHeight;

  const clonedSvg = svg.cloneNode(true);
  clonedSvg.setAttribute("x", "0");
  clonedSvg.setAttribute("y", String(headerHeight));
  clonedSvg.setAttribute("width", String(chartWidth));
  clonedSvg.setAttribute("height", String(chartHeight));
  clonedSvg.setAttribute("overflow", "visible");
  if (!clonedSvg.getAttribute("viewBox")) {
    clonedSvg.setAttribute("viewBox", `0 0 ${chartWidth} ${chartHeight}`);
  }

  const serializer = new XMLSerializer();
  const chartMarkup = serializer.serializeToString(clonedSvg);
  const source = [
    `<svg xmlns="http://www.w3.org/2000/svg" width="${exportWidth}" height="${exportHeight}" viewBox="0 0 ${exportWidth} ${exportHeight}">`,
    `<rect width="100%" height="100%" fill="#ffffff"/>`,
    `<text x="24" y="32" fill="#0f172a" font-size="22" font-weight="700" font-family="Segoe UI, Tahoma, sans-serif">${title}</text>`,
    subtitle
      ? `<text x="24" y="56" fill="#475569" font-size="13" font-family="Segoe UI, Tahoma, sans-serif">${subtitle}</text>`
      : "",
    scopeLabel
      ? `<text x="24" y="${subtitle ? 76 : 56}" fill="#64748b" font-size="12" font-family="Segoe UI, Tahoma, sans-serif">${scopeLabel}</text>`
      : "",
    chartMarkup,
    `</svg>`,
  ].join("");

  const blob = new Blob(
    ['<?xml version="1.0" encoding="UTF-8"?>\n', source],
    { type: "image/svg+xml;charset=utf-8" }
  );
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
}

function ChartDownloadButton({ containerRef, fileName, meta }) {
  return (
    <button
      type="button"
      onClick={() => downloadChartSvg(containerRef?.current, fileName, meta)}
      style={{
        border: "1px solid rgba(148, 163, 184, 0.24)",
        borderRadius: 10,
        padding: "8px 10px",
        background: "#fff",
        color: "#0f172a",
        fontSize: 13,
        fontWeight: 500,
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        cursor: "pointer",
      }}
      title="Скачать график в SVG"
    >
      <BsDownload aria-hidden="true" /> Скачать SVG
    </button>
  );
}

function ChartCard({ title, subtitle, children, minHeight = 280, extra, chartRef, scopeLabel }) {
  const fileName = `${sanitizeFileName(title)}.svg`;
  return (
    <div
      style={{
        background: "#ffffff",
        border: "1px solid rgba(148, 163, 184, 0.24)",
        borderRadius: 16,
        padding: 16,
        boxShadow: "0 10px 30px rgba(15, 23, 42, 0.08)",
        minHeight,
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start", marginBottom: 12 }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 600, color: "#0f172a" }}>{title}</div>
          {subtitle ? <div style={{ marginTop: 4, fontSize: 13, color: "#64748b" }}>{subtitle}</div> : null}
        </div>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
          {extra}
          {chartRef ? (
            <ChartDownloadButton
              containerRef={chartRef}
              fileName={fileName}
              meta={{ title, subtitle, scopeLabel }}
            />
          ) : null}
        </div>
      </div>
      <div ref={chartRef}>{children}</div>
    </div>
  );
}

function EmptyChartsState({ message }) {
  return (
    <div
      style={{
        height: 220,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        color: "#475569",
        fontSize: 14,
        border: "1px dashed rgba(148, 163, 184, 0.5)",
        borderRadius: 14,
        background: "rgba(248, 250, 252, 0.9)",
        padding: 20,
      }}
    >
      {message}
    </div>
  );
}

function OverviewChart({ data }) {
  const normalized = Array.isArray(data) ? data : [];
  return (
    <ResponsiveContainer width="100%" height={280}>
      <BarChart data={normalized} margin={{ top: 28, right: 20, left: 16, bottom: 16 }}>
        <CartesianGrid strokeDasharray="3 3" vertical={false} />
        <XAxis dataKey="name" tickLine={false} axisLine={false} />
        <YAxis allowDecimals={false} tickLine={false} axisLine={false}>
          <Label value="Количество ДТП" angle={-90} position="insideLeft" style={{ fill: "#475569", fontSize: 12 }} />
        </YAxis>
        <Tooltip formatter={(value) => [value, "ДТП"]} />
        <Bar dataKey="value" radius={[10, 10, 0, 0]}>
          <LabelList dataKey="value" position="top" fill="#0f172a" fontSize={12} fontWeight={600} />
          {normalized.map((entry) => (
            <Cell key={entry.name} fill={entry.name === "Период A" ? PERIOD_A_COLOR : PERIOD_B_COLOR} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

function TypesChart({ data }) {
  const normalized = Array.isArray(data) ? data : [];
  const height = Math.max(320, normalized.length * 52);
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={normalized} layout="vertical" margin={{ top: 12, right: 40, left: 24, bottom: 24 }}>
        <CartesianGrid strokeDasharray="3 3" horizontal={false} />
        <XAxis type="number" allowDecimals={false} tickLine={false} axisLine={false}>
          <Label value="Количество ДТП" position="insideBottom" offset={-8} style={{ fill: "#475569", fontSize: 12 }} />
        </XAxis>
        <YAxis type="category" dataKey="name" width={220} tickLine={false} axisLine={false} />
        <Tooltip formatter={(value) => [value, "ДТП"]} />
        <Legend />
        <Bar name="Период A" dataKey="periodA" fill={PERIOD_A_COLOR} radius={[0, 8, 8, 0]}>
          <LabelList dataKey="periodA" position="right" fill="#0f172a" fontSize={12} fontWeight={600} />
        </Bar>
        <Bar name="Период B" dataKey="periodB" fill={PERIOD_B_COLOR} radius={[0, 8, 8, 0]}>
          <LabelList dataKey="periodB" position="right" fill="#0f172a" fontSize={12} fontWeight={600} />
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

function SeveritySummary({ summary }) {
  if (!summary) return null;
  const items = [
    { label: "Баллы A", value: summary.periodAScore },
    { label: "Баллы B", value: summary.periodBScore },
    { label: "Коэфф. A", value: summary.periodACoefficient },
    { label: "Коэфф. B", value: summary.periodBCoefficient },
  ];
  return (
    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
      {items.map((item) => (
        <div
          key={item.label}
          style={{
            minWidth: 92,
            padding: "8px 10px",
            borderRadius: 12,
            background: "rgba(241, 245, 249, 0.95)",
            border: "1px solid rgba(148, 163, 184, 0.24)",
          }}
        >
          <div style={{ fontSize: 11, color: "#64748b" }}>{item.label}</div>
          <div style={{ marginTop: 2, fontSize: 16, fontWeight: 600, color: "#0f172a" }}>{item.value}</div>
        </div>
      ))}
    </div>
  );
}

function SeverityInfoButton() {
  const [open, setOpen] = useState(false);

  return (
    <div style={{ position: "relative" }}>
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        style={{
          width: 34,
          height: 34,
          borderRadius: 999,
          border: "1px solid rgba(148, 163, 184, 0.24)",
          background: "#fff",
          color: "#0f172a",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
        }}
        title="Как считаются баллы и коэффициент"
        aria-label="Как считаются баллы и коэффициент"
      >
        <BsQuestionCircle aria-hidden="true" />
      </button>

      {open ? (
        <div
          style={{
            position: "absolute",
            top: 42,
            right: 0,
            width: 320,
            padding: 14,
            borderRadius: 14,
            background: "#ffffff",
            border: "1px solid rgba(148, 163, 184, 0.24)",
            boxShadow: "0 14px 36px rgba(15, 23, 42, 0.18)",
            zIndex: 20,
            color: "#334155",
            fontSize: 13,
            lineHeight: 1.45,
          }}
        >
          <div style={{ fontSize: 14, fontWeight: 700, color: "#0f172a", marginBottom: 8 }}>
            Как считается тяжесть
          </div>
          <div>
            Баллы периода считаются как сумма весов всех ДТП:
            для ДТП <b>с пострадавшими</b> используется вес <b>3</b>, для ДТП <b>без пострадавших</b> вес <b>1</b>.
          </div>
          <div style={{ marginTop: 8 }}>
            Коэффициент тяжести = <b>сумма баллов / количество ДТП</b> за выбранный период.
          </div>
        </div>
      ) : null}
    </div>
  );
}

function SeverityChart({ data }) {
  const normalized = Array.isArray(data) ? data : [];
  return (
    <ResponsiveContainer width="100%" height={280}>
      <BarChart data={normalized} margin={{ top: 28, right: 20, left: 16, bottom: 16 }}>
        <CartesianGrid strokeDasharray="3 3" vertical={false} />
        <XAxis dataKey="name" tickLine={false} axisLine={false} />
        <YAxis allowDecimals={false} tickLine={false} axisLine={false}>
          <Label value="Количество ДТП" angle={-90} position="insideLeft" style={{ fill: "#475569", fontSize: 12 }} />
        </YAxis>
        <Tooltip formatter={(value) => [value, "ДТП"]} />
        <Legend />
        <Bar name="Период A" dataKey="periodA" fill={PERIOD_A_COLOR} radius={[10, 10, 0, 0]}>
          <LabelList dataKey="periodA" position="top" fill="#0f172a" fontSize={12} fontWeight={600} />
        </Bar>
        <Bar name="Период B" dataKey="periodB" fill={PERIOD_B_COLOR} radius={[10, 10, 0, 0]}>
          <LabelList dataKey="periodB" position="top" fill="#0f172a" fontSize={12} fontWeight={600} />
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

export default function DynamicAnalysisChartsPanel({ data }) {
  const [show, setShow] = useState(false);
  const overviewChartRef = useRef(null);
  const severityChartRef = useRef(null);
  const typesChartRef = useRef(null);

  const summaryText = useMemo(() => {
    if (!data?.scopeLabel) return "Графики";
    return data.scopeLabel;
  }, [data]);

  const noDataMessage = data?.message || "Недостаточно данных для построения графиков";
  const hasOverview = Array.isArray(data?.overview) && data.overview.length > 0;
  const hasTypes = Array.isArray(data?.types) && data.types.length > 0;
  const hasSeverity = Array.isArray(data?.severity) && data.severity.length > 0;

  return (
    <>
      <Button variant="dark" onClick={() => setShow(true)} className="buttons_custom_value">
        <BsGraphUpArrow className="icons_custom" /> Графики
      </Button>

      <Offcanvas show={show} onHide={() => setShow(false)} placement="bottom" style={{ height: "min(78vh, 760px)" }}>
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>Графики анализа динамики</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body style={{ background: "#f8fafc", overflowY: "auto" }}>
          <div style={{ fontSize: 13, color: "#64748b", marginBottom: 16 }}>{summaryText}</div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 16 }}>
            <ChartCard
              title="A vs B по общему числу ДТП"
              subtitle="Суммарное количество ДТП по выбранной области"
              chartRef={overviewChartRef}
              scopeLabel={summaryText}
            >
              {hasOverview ? <OverviewChart data={data.overview} /> : <EmptyChartsState message={noDataMessage} />}
            </ChartCard>

            <ChartCard
              title="A vs B по тяжести"
              subtitle="С пострадавшими, без пострадавших и коэффициент тяжести"
              extra={
                <>
                  <SeverityInfoButton />
                  <SeveritySummary summary={data?.severitySummary} />
                </>
              }
              chartRef={severityChartRef}
              scopeLabel={summaryText}
            >
              {hasSeverity ? <SeverityChart data={data.severity} /> : <EmptyChartsState message={noDataMessage} />}
            </ChartCard>
          </div>

          <div style={{ marginTop: 16 }}>
            <ChartCard
              title="A vs B по типам ДТП"
              subtitle="Сравнение структуры аварийности по типам ДТП"
              minHeight={360}
              chartRef={typesChartRef}
              scopeLabel={summaryText}
            >
              {hasTypes ? <TypesChart data={data.types} /> : <EmptyChartsState message={noDataMessage} />}
            </ChartCard>
          </div>
        </Offcanvas.Body>
      </Offcanvas>
    </>
  );
}
